import React, { CSSProperties, FC, PropsWithChildren, ReactNode, useState } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import OptionsDrawer from "../../../../components/OptionsDrawer";
import Tooltip from "../../../../components/Tooltip";

// styles
import css from "./Card.module.scss";

export interface IOptionsDrawerOption {
	id?: string;
	label: string;
	action: () => void;
}

interface CardProps {
	minHeight?: number;
	height?: number;
	padY?: number;
	padX?: number;
	testId?: string;
}

/**
 * Renders a card component with children with optional padding and a height
 * `height` is the height of the card
 * `padY` is the padding on the top and bottom of the card
 * `padX` is the padding on the left and right of the card
 *
 */
export const Card = (props: PropsWithChildren<CardProps>) => {
	const { children, height = 32, padY = 16, padX = 24, minHeight, testId } = props;
	const styleProp = {
		height: minHeight ? undefined : height - padY * 2,
		minHeight,
		padding: `${padY}px ${padX}px`,
	};
	return (
		<div className={css.card} style={styleProp} data-testid={testId}>
			<div className={css.cardContent}>{children}</div>
		</div>
	);
};

interface HeaderProps {
	options?: IOptionsDrawerOption[] | null;
	title?: ReactNode;
	toolBar?: ReactNode;
	info?: string;
	style?: object;
}

/**
 * Creates a card Header component with info icon and options drawer
 */
export const CardHeader = (props: HeaderProps) => {
	const { options, title = "", info = "", toolBar = "", style = {} } = props;
	const [isOpen, setIsOpen] = useState(false);
	return (
		<div className={css.cardHeader} style={style}>
			<span className={css.title}>
				{title}
				<span className={css.infoIcon}>
					<Tooltip message={info} placement="right">
						<FontAwesomeIcon icon={faCircleInfo} data-testid={faCircleInfo.iconName} />
					</Tooltip>
				</span>
			</span>
			<span>{toolBar}</span>
			{options ? (
				<span className={css.optionsDrawer}>
					<OptionsDrawer
						additionalClasses={`${css.optionsButton} ${isOpen ? css.optionsDrawerOpen : ""}`}
						openOn="bottom"
						horizontal={true}
						options={options}
						onSelect={(option) => option.action()}
						onOpen={setIsOpen}
					/>
				</span>
			) : null}
		</div>
	);
};

/**
 * Basically apllies card body styles to the children
 */
export const CardBody: FC<PropsWithChildren<{ style?: CSSProperties }>> = ({
	children,
	style,
}: PropsWithChildren<{ style?: CSSProperties }>) => {
	return (
		<div className={css.cardBody} style={style}>
			{children}
		</div>
	);
};
